// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Providers whose data is aerial imagery we can run a coverage check against. Anything else is a
// field-capture provider whose data is collected on site (never named to the customer).
import { ImageryRequestFlags, imageryHoldsSubmit, imageryRequestState } from 'Features/order/orderState';

// The imagery company the account's subscription tier is with (imagery_providers.company)
export const NEARMAP = 'Nearmap';
export const AERIAL_IMAGERY_PROVIDERS = [NEARMAP, 'Eagleview'];

type DataSectionProduct = { dataProvider?: string | null } | null | undefined;
type DataSectionProject = {
  tifFiles?: unknown[];
  imageryRequest?: ImageryRequestFlags | null;
} | null | undefined;

export interface DataSectionState {
  // 'requested': an open imagery request (pending or submitted) on a marketplace-aerial product — one card, the
  // request's state, no chooser (AUT-6630 F6); the imagery cost stays in the price.
  mode: 'chooser' | 'collapsed' | 'noDataComponent' | 'requested';
  showCoverageOption: boolean;
}

// True when the product's data comes from marketplace aerial imagery (vs. field capture,
// or no data component at all). These are the only products that need imagery in place —
// or requested, or supplied — before an order can be submitted.
export const isAerialImageryProduct = (product: DataSectionProduct): boolean => {
  const dataProvider = product?.dataProvider ?? null;
  return !!dataProvider && AERIAL_IMAGERY_PROVIDERS.includes(dataProvider);
};

// True when the product's data is collected on site by a field-capture provider. These
// products carry no firm price/turnaround at checkout — capture is coordinated after the
// order is placed. A product with no provider at all (e.g. Landbase) is neither aerial
// nor field-capture.
export const isFieldCaptureProduct = (product: DataSectionProduct): boolean => {
  const dataProvider = product?.dataProvider ?? null;
  return !!dataProvider && !AERIAL_IMAGERY_PROVIDERS.includes(dataProvider);
};

// Decides what the Data step shows for the selected product and the project's data state.
// - 'noDataComponent': the product carries no data-provider option, so there is no sourcing
//   choice and no data cost — nothing to ask the customer.
// - 'collapsed': the project already has imagery on file (uploaded rasters, or a fulfilled
//   imagery request), so extraction runs on what's there — marketplace data is neither
//   offered nor charged. The project is the container for its data: fresh imagery means a
//   fresh project. Only for marketplace-aerial products; existing imagery says nothing
//   about a field-capture product's site data.
// - 'requested': an open (pending or submitted) imagery request on a marketplace-aerial
//   product — one card with the request's state in place of the chooser (AUT-6630 F6). It
//   deliberately does NOT collapse: the order that requested the imagery carries its data
//   cost, and the data step forces own-data off while the request is open so the price —
//   saved, held-submitted and placed — always carries it.
// - 'chooser': the two-card choice.
export const resolveDataSection = (product: DataSectionProduct, project: DataSectionProject): DataSectionState => {
  const dataProvider = product?.dataProvider ?? null;
  const isAerialImageryProvider = isAerialImageryProduct(product);
  // One rule for the request's state (orderState): fulfilled means imagery on the project; an
  // open request (pending or submitted) is what hides the coverage card; rejected reads as none.
  const requestState = imageryRequestState(project?.imageryRequest);
  const projectHasImagery = (project?.tifFiles?.length ?? 0) > 0 || requestState === 'fulfilled';
  const pendingImageryRequest = imageryHoldsSubmit(requestState);

  if (product && !dataProvider) {
    return { mode: 'noDataComponent', showCoverageOption: false };
  }
  if (isAerialImageryProvider && projectHasImagery) {
    return { mode: 'collapsed', showCoverageOption: false };
  }
  if (isAerialImageryProvider && pendingImageryRequest) {
    return { mode: 'requested', showCoverageOption: false };
  }
  return { mode: 'chooser', showCoverageOption: isAerialImageryProvider };
};

// A cart of several products (AUT-6630 D10): one data class per product, read from the combination's products, or the
// one product's. The step shows one card per class, the hold and the copy read whether a marketplace class is present.
export type CartDataProduct = { name: string; dataProvider: string | null; subtype?: string };
type CartProduct = (DataSectionProduct & {
  name?: string;
  subtype?: string;
  products?: { _id: string; name: string; dataProvider?: string | null; subtype?: string }[];
}) | null | undefined;

export const cartProducts = (product: CartProduct): CartDataProduct[] => {
  if (!product) return [];
  if (product.products?.length) {
    return product.products.map((entry) => ({ name: entry.name, dataProvider: entry.dataProvider ?? null, subtype: entry.subtype }));
  }
  return [{ name: product.name ?? '', dataProvider: product.dataProvider ?? null, subtype: product.subtype }];
};

export const hasMarketplaceClass = (product: CartProduct): boolean => cartProducts(product).some((entry) => isAerialImageryProduct(entry));

export const fieldCaptureProductNames = (product: CartProduct): string[] => (
  cartProducts(product).filter((entry) => isFieldCaptureProduct(entry)).map((entry) => entry.name)
);

export const noDataProductNames = (product: CartProduct): string[] => (
  cartProducts(product).filter((entry) => !entry.dataProvider).map((entry) => entry.name)
);

// "A", "A and B", "A, B and C"
export const listNames = (names: string[]): string => {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

export interface DataSectionsState {
  // the marketplace class: its one-class state, the provider the cart priced, the first marketplace product's subtype
  // (the card's sentence names it) and the products in the class
  marketplace: (DataSectionState & { provider: string; subtype?: string; names: string[] }) | null;
  fieldCapture: { names: string[] } | null;
  // whole: every product of the cart is reference data (today's single-product note)
  noData: { names: string[]; whole: boolean } | null;
  // the own-data chooser: the marketplace class's mode decides when there is one, else a field-capture class shows it
  showChooser: boolean;
  // the coverage option belongs to an open marketplace class
  showCoverageOption: boolean;
}

export const resolveDataSections = (product: CartProduct, project: DataSectionProject): DataSectionsState => {
  const products = cartProducts(product);
  if (!products.length) {
    const plain = resolveDataSection(null, project);
    return { marketplace: null, fieldCapture: null, noData: null, showChooser: plain.mode === 'chooser', showCoverageOption: plain.showCoverageOption };
  }
  const marketplaceProducts = products.filter((entry) => isAerialImageryProduct(entry));
  const fieldNames = products.filter((entry) => isFieldCaptureProduct(entry)).map((entry) => entry.name);
  const noDataNames = products.filter((entry) => !entry.dataProvider).map((entry) => entry.name);
  const marketplace = marketplaceProducts.length
    ? {
      ...resolveDataSection({ dataProvider: marketplaceProducts[0].dataProvider }, project),
      provider: marketplaceProducts[0].dataProvider as string,
      subtype: marketplaceProducts[0].subtype,
      names: marketplaceProducts.map((entry) => entry.name),
    }
    : null;
  const fieldCapture = fieldNames.length ? { names: fieldNames } : null;
  const noData = noDataNames.length ? { names: noDataNames, whole: noDataNames.length === products.length } : null;
  const showChooser = marketplace ? marketplace.mode === 'chooser' : !!fieldCapture;
  return {
    marketplace,
    fieldCapture,
    noData,
    showChooser,
    showCoverageOption: !!marketplace && marketplace.mode === 'chooser' && marketplace.showCoverageOption,
  };
};
